// Privacy notice. Content provided verbatim - only structural/typographic
// formatting is added by this component.

import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-dkgreen px-9 py-8">
        <a
          href="/"
          className="inline-flex items-center gap-2 font-sans text-[11px] font-medium tracking-[0.18em] uppercase text-ltgreen mb-2 hover:text-cream transition-colors"
        >
          <ArrowLeft size={12} /> Back
        </a>
        <h1 className="font-display text-3xl font-extrabold text-cream leading-tight">
          Privacy
        </h1>
      </header>

      <main className="px-9 py-10 max-w-2xl text-[15px] leading-[1.7] text-asphalt">
        <p className="text-muted text-sm mb-8">
          Last updated 26 April 2026.
        </p>

        <Section heading="What we collect">
          <p>When you use the tool, we collect:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted">
            <li>The job title and organisation description you enter</li>
            <li>The email address you submit</li>
            <li>Your IP address and country</li>
            <li>A session cookie used for rate limiting and abuse prevention</li>
            <li>Anonymous usage events (e.g. which steps you reach) via Mixpanel</li>
          </ul>
        </Section>

        <Section heading="Why we collect it">
          <ul className="list-disc pl-5 space-y-1 text-muted">
            <li>Your inputs are sent to Anthropic's Claude API to generate the output</li>
            <li>Your email is used to send you the briefing and follow up if you request it</li>
            <li>The session cookie supports rate limiting and basic security</li>
            <li>Analytics help us understand where the experience breaks or drops off</li>
          </ul>
          <p className="mt-4">We do not use your data for advertising.</p>
          <p>We do not sell it.</p>
          <p>We do not share it beyond what is required to run the service.</p>
        </Section>

        <Section heading="Who processes it">
          <p>We use the following processors:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted">
            <li><strong className="text-asphalt font-semibold">Cloudflare</strong> - hosting and API infrastructure</li>
            <li><strong className="text-asphalt font-semibold">Anthropic</strong> - model inference (Claude)</li>
            <li><strong className="text-asphalt font-semibold">Resend</strong> - email delivery</li>
            <li><strong className="text-asphalt font-semibold">Calendly</strong> - scheduling</li>
            <li><strong className="text-asphalt font-semibold">Mixpanel</strong> - product analytics</li>
          </ul>
          <p className="mt-4">
            Each provider only receives the minimum data required to perform its function under standard data processing terms.
          </p>
        </Section>

        <Section heading="How long we keep it">
          <ul className="list-disc pl-5 space-y-1 text-muted">
            <li>Email and submission data: until deletion is requested, or after 24 months of inactivity</li>
            <li>Session cookies: up to 7 days</li>
            <li>Rate limit data: up to 24 hours</li>
            <li>Analytics data: retained according to Mixpanel's standard retention (typically ~12 months)</li>
          </ul>
        </Section>

        <Section heading="Cookies">
          <p>We use:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted">
            <li>A strictly necessary session cookie for rate limiting and security</li>
            <li>Analytics cookies set by Mixpanel</li>
          </ul>
          <p className="mt-4">By using the site, you acknowledge the use of these cookies.</p>
        </Section>

        <Section heading="Your rights">
          <p>You can request:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted">
            <li>A copy of the data we hold on you</li>
            <li>Deletion of your data</li>
          </ul>
          <p className="mt-4">
            Contact: <a href="mailto:info@sagentivum.com" className="text-dkgreen underline decoration-dkgreen/40 hover:decoration-dkgreen">info@sagentivum.com</a>
          </p>
          <p>We aim to respond within ten working days.</p>
          <p className="mt-4">
            If you are not satisfied, you can raise a complaint with your local data protection authority.
          </p>
        </Section>
      </main>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="font-display text-lg font-bold text-asphalt mb-2">{heading}</h2>
      <div className="space-y-3 text-muted">{children}</div>
    </section>
  );
}
